/**
 * Types pour le système de Comptes-Rendus de Réunion (CR)
 * Utilisé par Otto, les blocs interactifs et l'edge function otto-meeting-report
 */

export type MeetingReportStatus =
  | 'draft'
  | 'transcript_fetched'
  | 'cr_generated'
  | 'actions_validated'
  | 'email_sent'
  | 'error'
  /** Terminal: no verified meeting with a client (cancelled, no Meet session in the slot, client absent, transcript too short). No automatic CR. */
  | 'no_meeting';

export type ExtractedActionType = 'task' | 'reminder' | 'appointment';

export interface ExtractedAction {
  type: ExtractedActionType;
  title: string;
  description?: string;
  assignee?: string | null;
  due_date?: string | null;
  priority: 'low' | 'medium' | 'high' | 'critical';
  /** ID de l'entite CRM creee automatiquement (task, feature, etc.) */
  created_entity_id?: string | null;
  /** Table de l'entite creee (ex. 'crm_tasks') */
  created_entity_type?: string | null;
}

export interface MeetingReport {
  id: string;
  appointment_id: string | null;
  note_id: string | null;
  google_meet_conference_id: string | null;
  transcript_raw: string | null;
  transcript_fetched_at: string | null;
  cr_title: string | null;
  cr_html: string | null;
  cr_markdown: string | null;
  cr_generated_at: string | null;
  prompt_template_used: string | null;
  extracted_actions: ExtractedAction[];
  validated_actions: ExtractedAction[];
  actions_validated_at: string | null;
  email_sent_to: string | null;
  email_sent_at: string | null;
  email_resend_id: string | null;
  status: MeetingReportStatus;
  error_message: string | null;
  owner_id: string;
  created_at: string;
  updated_at: string;
}

/** Données retournées par meetingSaveCR, utilisées par les blocs interactifs Otto */
export interface MeetingReportData {
  report_id: string;
  cr_title: string;
  cr_html: string;
  cr_markdown: string;
  extracted_actions: ExtractedAction[];
  contact_email?: string;
  contact_name?: string;
  account_id?: string | null;
  note_id?: string | null;
}

/** Labels français pour les types d'actions */
export const EXTRACTED_ACTION_TYPE_LABELS: Record<ExtractedActionType, string> = {
  task: 'Tâche',
  reminder: 'Rappel',
  appointment: 'Rendez-vous',
};

/** Couleurs Tailwind pour les types d'actions */
export const EXTRACTED_ACTION_TYPE_COLORS: Record<ExtractedActionType, string> = {
  task: 'bg-emerald-100 text-emerald-700',
  reminder: 'bg-rose-100 text-rose-700',
  appointment: 'bg-blue-100 text-[#0066FF]',
};

/** Labels français pour les priorités */
export const PRIORITY_LABELS: Record<string, string> = {
  low: 'Basse',
  medium: 'Moyenne',
  high: 'Haute',
  critical: 'Critique',
};

export const PRIORITY_COLORS: Record<string, string> = {
  low: 'text-neutral-500',
  medium: 'text-[#0066FF]',
  high: 'text-orange-600',
  critical: 'text-red-600',
};
